import React, { useEffect, useMemo, useState } from "react";
import Nouislider from "nouislider-react";
import "nouislider/distribute/nouislider.css";
import { Collapse, Button, Card, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import type {
  StoreAttributeFilterGroupOut,
  StoreBrandFilterOptionOut,
  StoreDiscountFilterOptionOut,
  StoreProductAvailableFiltersOut,
} from "../../redux/services/store/storeProductsApi";

type SelectedFilters = {
  q?: string;
  brand_id?: number | null;
  price_min?: number | null;
  price_max?: number | null;
  discount_min?: number | null;
  attributes?: Record<string, number[]>;
};

type Props = {
  name?: string;
  availableFilters?: StoreProductAvailableFiltersOut | null;
  selectedFilters?: SelectedFilters;
  onChangeFilters?: (next: SelectedFilters) => void;
};

const emptyFilters: StoreProductAvailableFiltersOut = {
  brands: [],
  attributes: [],
  price: null,
  stock: { in_stock: 0, out_of_stock: 0 },
  discount_ranges: [],
};

const toNumber = (v: unknown, fallback = 0): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeToken = (value: unknown): string =>
  String(value ?? "").trim().toLowerCase();

const isSingleSelectAttributeGroup = (group: StoreAttributeFilterGroupOut) =>
  group.display_layout === "color_swatch" ||
  normalizeToken(group.code) === "color" ||
  normalizeToken(group.code) === "colour";

const Filters = ({
  name,
  availableFilters,
  selectedFilters,
  onChangeFilters,
}: Props) => {
  const filters = availableFilters ?? emptyFilters;

  const brandsList = filters.brands ?? [];
  const attributeGroups = filters.attributes ?? [];
  const discountRanges = filters.discount_ranges ?? [];
  const priceFilter = filters.price ?? null;

  const minPriceFromApi = useMemo(
    () => toNumber(priceFilter?.min, 0),
    [priceFilter]
  );
  const maxPriceFromApi = useMemo(
    () => toNumber(priceFilter?.max, 0),
    [priceFilter]
  );

  const [searchText, setSearchText] = useState(selectedFilters?.q ?? "");
  const [brandSearch, setBrandSearch] = useState("");

  const [priceOpen, setPriceOpen] = useState(true);
  const [brandsOpen, setBrandsOpen] = useState(true);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [attributeOpenMap, setAttributeOpenMap] = useState<Record<string, boolean>>({});

  const [mincost, setMincost] = useState<number>(
    selectedFilters?.price_min ?? minPriceFromApi
  );
  const [maxcost, setMaxcost] = useState<number>(
    selectedFilters?.price_max ?? maxPriceFromApi
  );

  useEffect(() => {
    setSearchText(selectedFilters?.q ?? "");
  }, [selectedFilters?.q]);

  useEffect(() => {
    const nextMin = selectedFilters?.price_min ?? minPriceFromApi;
    const nextMax = selectedFilters?.price_max ?? maxPriceFromApi;
    setMincost(nextMin);
    setMaxcost(nextMax);
  }, [
    selectedFilters?.price_min,
    selectedFilters?.price_max,
    minPriceFromApi,
    maxPriceFromApi,
  ]);

  useEffect(() => {
    const initial: Record<string, boolean> = {};
    for (const group of attributeGroups) {
      initial[group.code] = true;
    }
    setAttributeOpenMap((prev) => ({ ...initial, ...prev }));
  }, [attributeGroups]);

  const filteredBrands = useMemo(() => {
    const q = brandSearch.trim().toLowerCase();
    if (!q) return brandsList;
    return brandsList.filter((b) =>
      (b.label || "").toLowerCase().includes(q)
    );
  }, [brandSearch, brandsList]);

  const selectedBrandId = selectedFilters?.brand_id ?? null;
  const selectedDiscountMin = selectedFilters?.discount_min ?? null;
  const selectedAttributes = selectedFilters?.attributes ?? {};

  const emitFilters = (patch: Partial<SelectedFilters>) => {
    if (!onChangeFilters) return;
    onChangeFilters({
      q: selectedFilters?.q ?? "",
      brand_id: selectedFilters?.brand_id ?? null,
      price_min: selectedFilters?.price_min ?? null,
      price_max: selectedFilters?.price_max ?? null,
      discount_min: selectedFilters?.discount_min ?? null,
      attributes: selectedFilters?.attributes ?? {},
      ...patch,
    });
  };

  const toggleAttributeValue = (
    group: StoreAttributeFilterGroupOut,
    valueId: number
  ) => {
    const currentValues = selectedAttributes[group.code] ?? [];
    const exists = currentValues.includes(valueId);
    const nextValues = isSingleSelectAttributeGroup(group)
      ? exists
        ? []
        : [valueId]
      : exists
        ? currentValues.filter((id) => id !== valueId)
        : [...currentValues, valueId];

    const nextAttributes = {
      ...selectedAttributes,
      [group.code]: nextValues,
    };

    if (nextValues.length === 0) {
      delete nextAttributes[group.code];
    }

    emitFilters({ attributes: nextAttributes });
  };

  const clearAll = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setSearchText("");
    setBrandSearch("");
    setMincost(minPriceFromApi);
    setMaxcost(maxPriceFromApi);

    emitFilters({
      q: "",
      brand_id: null,
      price_min: null,
      price_max: null,
      discount_min: null,
      attributes: {},
    });
  };

  const applySearch = () => {
    emitFilters({ q: searchText.trim() || "" });
  };

  const onPriceSlide = (value: (number | string)[]) => {
    const minVal = toNumber(value?.[0], minPriceFromApi);
    const maxVal = toNumber(value?.[1], maxPriceFromApi);
    setMincost(minVal);
    setMaxcost(maxVal);
  };

  const applyPrice = () => {
    const nextMin = Math.max(minPriceFromApi, Math.min(mincost, maxcost));
    const nextMax = Math.min(maxPriceFromApi, Math.max(mincost, maxcost));
    emitFilters({
      price_min: nextMin,
      price_max: nextMax,
    });
  };

  const renderAttributeValues = (group: StoreAttributeFilterGroupOut) => {
    const values = group.values ?? [];
    if (!values.length) return null;

    const isColorLike =
      isSingleSelectAttributeGroup(group);

    if (isColorLike) {
      return (
        <ul className="clothe-colors list-unstyled hstack gap-2 mb-0 flex-wrap">
          {values.map((value, idx) => {
            const checked = (selectedAttributes[group.code] ?? []).includes(value.id);
            const inputId = `attr-${group.code}-${value.id}`;

            const classes = [
              "btn btn-info",
              "btn btn-light",
              "btn btn-primary",
              "btn btn-success",
              "btn btn-warning",
              "btn btn-danger",
            ];
            const cls = classes[idx % classes.length];

            return (
              <li key={value.id} title={`${value.label} (${value.count})`}>
                <Form.Check
                  type="checkbox"
                  id={inputId}
                  checked={checked}
                  onChange={() => toggleAttributeValue(group, value.id)}
                  className="d-none"
                />
                <Form.Label
                  className={`avatar-xs p-0 d-flex align-items-center justify-content-center rounded-circle filter-color-swatch ${
                    value.hex_color ? "" : cls
                  } ${checked ? "active" : ""}`}
                  htmlFor={inputId}
                  style={
                    value.hex_color
                      ? {
                          backgroundColor: value.hex_color,
                        }
                      : undefined
                  }
                >
                  <span className="visually-hidden">{value.label}</span>
                </Form.Label>
              </li>
            );
          })}
        </ul>
      );
    }

    const isButtonLike =
      group.display_layout === "button" ||
      group.code.toLowerCase() === "size";

    if (isButtonLike) {
      return (
        <ul className="clothe-size list-unstyled hstack gap-2 mb-0 flex-wrap">
          {values.map((value) => {
            const checked = (selectedAttributes[group.code] ?? []).includes(value.id);
            const inputId = `attr-${group.code}-${value.id}`;
            return (
              <li key={value.id}>
                <Form.Check
                  type="checkbox"
                  id={inputId}
                  checked={checked}
                  onChange={() => toggleAttributeValue(group, value.id)}
                  className="d-none"
                />
                <Form.Label
                  htmlFor={inputId}
                  className={`avatar-xs btn p-0 d-flex align-items-center justify-content-center rounded-circle ${
                    checked ? "btn-primary text-white" : "btn-soft-primary"
                  }`}
                  style={{ cursor: "pointer" }}
                >
                  {value.label}
                </Form.Label>
              </li>
            );
          })}
        </ul>
      );
    }

    return (
      <div className="d-flex flex-column gap-2 filter-check">
        {values.map((value) => {
          const checked = (selectedAttributes[group.code] ?? []).includes(value.id);
          const inputId = `attr-${group.code}-${value.id}`;
          return (
            <div className="form-check" key={value.id}>
              <Form.Check
                type="checkbox"
                id={inputId}
                checked={checked}
                onChange={() => toggleAttributeValue(group, value.id)}
              />
              <Form.Label className="form-check-label" htmlFor={inputId}>
                {value.label}
                <span className="text-muted ms-2">({value.count})</span>
              </Form.Label>
            </div>
          );
        })}
      </div>
    );
  };

  const showPrice = !!priceFilter && maxPriceFromApi >= minPriceFromApi;
  const showBrands = brandsList.length > 0;
  const showDiscount = discountRanges.length > 0;
  const showAttributes = attributeGroups.some((g) => (g.values ?? []).length > 0);

  return (
    <React.Fragment>
      <style>
        {`
          .catalog-filters-card {
            border-radius: 20px !important;
            border: 1px solid rgba(0, 0, 0, 0.08) !important;
            box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
            overflow: hidden;
          }

          .catalog-filters-card .card-header {
            background: #fff;
            padding: 18px 18px 16px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.06);
          }

          .catalog-filters-title {
            font-size: 16px;
            font-weight: 700;
            color: #111827;
          }

          .catalog-filters-clear {
            font-size: 13px;
            font-weight: 600;
            color: #111827;
          }

          .catalog-filters-search {
            display: flex;
            gap: 10px;
          }

          .catalog-filters-search .form-control {
            min-height: 44px;
            border-radius: 12px;
          }

          .catalog-filters-search .btn {
            min-width: 96px;
            min-height: 44px;
            border-radius: 12px;
            font-weight: 600;
          }

          .catalog-filters-card .accordion-button {
            padding: 14px 18px !important;
            font-weight: 600;
          }

          .catalog-filters-card .accordion-button:not(.collapsed) {
            background: transparent !important;
            color: inherit !important;
            box-shadow: none !important;
          }

          .catalog-filters-card .accordion-button:focus {
            box-shadow: none !important;
          }

          .catalog-filters-card .accordion-body {
            padding: 0 18px 16px 18px;
          }

          .catalog-filters-card .accordion-item {
            border: 0 !important;
          }

          .catalog-filters-card .accordion-item + .accordion-item {
            border-top: 1px solid rgba(0, 0, 0, 0.06) !important;
          }

          .catalog-brand-search .form-control {
            min-height: 42px;
            border-radius: 12px;
          }

          .catalog-brand-search .search-icon {
            top: 50%;
            transform: translateY(-50%);
          }

          .catalog-price-input {
            min-height: 40px;
            border-radius: 10px;
          }

          .catalog-price-apply {
            min-height: 40px;
            border-radius: 10px;
            font-weight: 600;
            white-space: nowrap;
          }

          .filter-color-swatch {
            width: 34px !important;
            height: 34px !important;
            cursor: pointer;
            border: 1px solid rgba(0, 0, 0, 0.12);
            box-shadow: 0 6px 14px rgba(0, 0, 0, 0.06);
            transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
          }

          .filter-color-swatch.active {
            transform: scale(1.08);
            border-color: #111 !important;
            box-shadow: 0 0 0 2px rgba(17, 17, 17, 0.15);
          }

          @media (max-width: 767.98px) {
            .catalog-filters-card {
              border-radius: 0 !important;
              border: 0 !important;
              box-shadow: none !important;
            }

            .catalog-filters-card .card-header {
              padding: 0 0 14px;
              border-bottom: 1px solid rgba(0, 0, 0, 0.06);
            }

            .catalog-filters-card .accordion-button {
              padding-left: 0 !important;
              padding-right: 0 !important;
            }

            .catalog-filters-card .accordion-body {
              padding-left: 0;
              padding-right: 0;
            }

            .catalog-filters-search {
              flex-direction: column;
            }

            .catalog-filters-search .btn {
              width: 100%;
            }

            .catalog-price-row {
              flex-wrap: wrap;
            }

            .catalog-price-row .form-control,
            .catalog-price-row .btn {
              width: 100%;
            }
          }
        `}
      </style>

      <div className={name || ""}>
        <Card className="catalog-filters-card">
          <Card.Header>
            <div className="d-flex align-items-center mb-3 gap-2">
              <div className="flex-grow-1">
                <h5 className="catalog-filters-title mb-0">Filters</h5>
              </div>
              <div className="flex-shrink-0">
                <Link
                  to="#"
                  className="text-decoration-underline catalog-filters-clear"
                  id="clearall"
                  onClick={clearAll}
                >
                  Clear All
                </Link>
              </div>
            </div>

            <div className="catalog-filters-search">
              <Form.Control
                id="searchProductList"
                autoComplete="off"
                placeholder="Search Products..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") applySearch();
                }}
              />
              <Button variant="dark" onClick={applySearch}>
                Search
              </Button>
            </div>
          </Card.Header>

          <div className="accordion accordion-flush filter-accordion">
            {showPrice && (
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Button
                    onClick={() => setPriceOpen(!priceOpen)}
                    className="accordion-button bg-transparent shadow-none"
                    aria-expanded={priceOpen}
                  >
                    <span className="text-muted text-uppercase fs-12 fw-medium">
                      Price
                    </span>
                  </Button>
                </h2>
                <Collapse in={priceOpen}>
                  <div>
                    <div className="accordion-body text-body pt-0">
                      <Nouislider
                        range={{ min: minPriceFromApi, max: maxPriceFromApi }}
                        start={[mincost, maxcost]}
                        connect
                        onSlide={onPriceSlide}
                        data-slider-color="info"
                        id="product-price-range"
                      />
                      <div className="catalog-price-row d-flex gap-2 align-items-center mt-3">
                        <Form.Control
                          className="catalog-price-input form-control-sm"
                          value={mincost}
                          onChange={(e) =>
                            setMincost(toNumber(e.target.value, minPriceFromApi))
                          }
                        />
                        <span className="fw-semibold text-muted">to</span>
                        <Form.Control
                          className="catalog-price-input form-control-sm"
                          value={maxcost}
                          onChange={(e) =>
                            setMaxcost(toNumber(e.target.value, maxPriceFromApi))
                          }
                        />
                        <Button
                          size="sm"
                          className="catalog-price-apply"
                          onClick={applyPrice}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </div>
                </Collapse>
              </div>
            )}

            {showBrands && (
              <div className="accordion-item">
                <h2 className="accordion-header" id="flush-headingBrands">
                  <Button
                    onClick={() => setBrandsOpen(!brandsOpen)}
                    className="accordion-button bg-transparent shadow-none"
                    aria-controls="flush-collapseBrands"
                    aria-expanded={brandsOpen}
                  >
                    <span className="text-muted text-uppercase fs-12 fw-medium">
                      Brands
                    </span>
                  </Button>
                </h2>
                <Collapse in={brandsOpen}>
                  <div id="flush-collapseBrands">
                    <div
                      className="accordion-collapse collapse show"
                      aria-labelledby="flush-headingBrands"
                    >
                      <div className="accordion-body text-body pt-0">
                        <div className="search-box search-box-sm catalog-brand-search">
                          <Form.Control
                            type="text"
                            className="bg-light border-0"
                            placeholder="Search Brands..."
                            value={brandSearch}
                            onChange={(e) => setBrandSearch(e.target.value)}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>

                        <div className="d-flex flex-column gap-2 mt-3 filter-check">
                          {filteredBrands.map((brand: StoreBrandFilterOptionOut) => {
                            const id = `brand-${brand.id}`;
                            return (
                              <div className="form-check" key={brand.id}>
                                <Form.Check
                                  type="radio"
                                  name="brandFilter"
                                  id={id}
                                  checked={selectedBrandId === brand.id}
                                  onChange={() => emitFilters({ brand_id: brand.id })}
                                />
                                <Form.Label
                                  className="form-check-label"
                                  htmlFor={id}
                                >
                                  {brand.label}
                                  <span className="text-muted ms-2">
                                    ({brand.count})
                                  </span>
                                </Form.Label>
                              </div>
                            );
                          })}

                          {selectedBrandId !== null && (
                            <div className="pt-1">
                              <Button
                                variant="link"
                                className="text-decoration-none fw-medium p-0"
                                onClick={() => emitFilters({ brand_id: null })}
                              >
                                Clear brand
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Collapse>
              </div>
            )}

            {showAttributes &&
              attributeGroups.map((group) => {
                const values = group.values ?? [];
                if (!values.length) return null;

                const isOpen = attributeOpenMap[group.code] ?? true;
                const headingId = `heading-${group.code}`;
                const collapseId = `collapse-${group.code}`;

                return (
                  <div className="accordion-item" key={group.attribute_id}>
                    <h2 className="accordion-header" id={headingId}>
                      <Button
                        onClick={() =>
                          setAttributeOpenMap((prev) => ({
                            ...prev,
                            [group.code]: !isOpen,
                          }))
                        }
                        className="accordion-button bg-transparent shadow-none"
                        aria-controls={collapseId}
                        aria-expanded={isOpen}
                      >
                        <span className="text-muted text-uppercase fs-12 fw-medium">
                          {group.label}
                        </span>
                      </Button>
                    </h2>
                    <Collapse in={isOpen}>
                      <div id={collapseId}>
                        <div className="accordion-body text-body pt-0">
                          {renderAttributeValues(group)}
                        </div>
                      </div>
                    </Collapse>
                  </div>
                );
              })}

            {showDiscount && (
              <div className="accordion-item">
                <h2 className="accordion-header" id="flush-headingDiscount">
                  <Button
                    onClick={() => setDiscountOpen(!discountOpen)}
                    aria-controls="flush-collapseDiscount"
                    aria-expanded={discountOpen}
                    className="accordion-button bg-transparent shadow-none"
                  >
                    <span className="text-muted text-uppercase fs-12 fw-medium">
                      Discount
                    </span>
                  </Button>
                </h2>
                <Collapse in={discountOpen}>
                  <div id="flush-collapseDiscount">
                    <div
                      className="accordion-collapse collapse show"
                      aria-labelledby="flush-headingDiscount"
                    >
                      <div className="accordion-body text-body pt-1">
                        <div className="d-flex flex-column gap-2 filter-check">
                          {discountRanges.map((item: StoreDiscountFilterOptionOut) => {
                            const id = `discount-${item.value}`;
                            return (
                              <div className="form-check" key={item.value}>
                                <Form.Check
                                  type="radio"
                                  name="discountFilter"
                                  id={id}
                                  checked={selectedDiscountMin === item.value}
                                  onChange={() =>
                                    emitFilters({ discount_min: item.value })
                                  }
                                />
                                <Form.Label
                                  className="form-check-label"
                                  htmlFor={id}
                                >
                                  {item.label}
                                  <span className="text-muted ms-2">
                                    ({item.count})
                                  </span>
                                </Form.Label>
                              </div>
                            );
                          })}

                          {selectedDiscountMin !== null && (
                            <div className="pt-1">
                              <Button
                                variant="link"
                                className="text-decoration-none fw-medium p-0"
                                onClick={() => emitFilters({ discount_min: null })}
                              >
                                Clear discount
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Collapse>
              </div>
            )}
          </div>
        </Card>
      </div>
    </React.Fragment>
  );
};

export default Filters;
